/** @jsxImportSource @emotion/react */

import { useEffect, useRef, useMemo, useReducer } from "react";
import { Rectangle, Text, Circle, Arrow, Player, Polygon } from "../shapes";
import PropTypes from "prop-types";
import Helper from "../functions/Helper.js";
import interact from "interactjs";
import Court from "../courts/Court.js";
import PhaseWrapper from "./PhaseWrapper.js";
import {
  unselect,
  addObject,
  selectMultiple,
} from "../reducers/slices/phases.js";
import { useDimensions } from "../../hooks/useDimensions";
import useCurrentValue from "../../hooks/useCurrentValue.js";
import SelectionRectangle from "./SelectionRectangle.js";
import {
  selectObjects,
  getSelectionRect,
} from "../functions/collisionChecking.js";

const transformToSVG = (x, y) => {
  let svg = document.querySelector("#editor_court");
  if (!svg) return [];
  let pt = svg.createSVGPoint();
  pt.x = x;
  pt.y = y;
  pt = pt.matrixTransform(svg.getScreenCTM().inverse());
  return [pt.x, pt.y];
};

const statusPriorities = {
  NOT_SELECTED: 0,
  HOVERING: 1,
  SELECTED_MULTIPLE: 1,
  HOVERING_ANIMATOR: 1,
  SELECTED: 2,
  EDIT: 3,
  EDIT_MOBILE: 3,
  INTERACTING: 4,
};

const getStatusPriority = (x) => {
  return statusPriorities[x] ?? 0;
};

const getSortedObjects = (objects, court) => {
  let transformedObjects = Helper.transformObjects(objects, court);
  transformedObjects.sort((a, b) => {
    let statusDiff = getStatusPriority(a.status) - getStatusPriority(b.status);
    if (statusDiff === 0) {
      //return getSubTypePriority(a.subType) - getSubTypePriority(b.subType);
      return a.displayOrder - b.displayOrder;
    } else return statusDiff;
  });
  return transformedObjects;
};

function Phase(props) {
  const { ref: wrapper, dimensions: wrapperDimensions } = useDimensions();
  const wrapperWidth = props.responsive ? wrapperDimensions.width : props.width;
  const height = Helper.getHeight(wrapperWidth, props.court);
  const courtRef = useCurrentValue(props.court);

  useEffect(() => {
    if (props.editable) {
      interact("#" + props.uId + "_court_bg")
        .styleCursor(false)
        /*
        .on("tap", (event) => {
          props.dispatch(unselect());
        })*/
        .draggable({
          onend: () => props.dispatch(unselect()),
        });

      interact("#" + props.uId + "_court").dropzone({
        // only accept elements matching this CSS selector
        accept: ".phase_droppable",
        //  ".phase_droppable,.drag_icon,.drag_arrow,#drag_handoff,#drag_handoff_mobile,#drag_cone_icon,#drag_cone_icon_mobile,#drag_text_icon,#drag_text_icon_mobile,#drag_rectangle_icon,#drag_rectangle_icon_mobile,#drag_circle_icon,#drag_circle_icon_mobile,#drag_triangle_icon,#drag_triangle_icon_mobile,#drag_polygon_icon,#drag_polygon_icon_mobile",
        // Require a 30% element overlap for a drop to be possible
        overlap: 0.01,
        // listen for drop related events:
        ondrop: (event) => {
          let svg = document.querySelector("#editor_court");
          let pt = svg.createSVGPoint();
          let metaKey = event.dragEvent.metaKey;
          let dataSubType = event.relatedTarget.dataset.subType;
          let dataStance = event.relatedTarget.dataset.stance ?? "regular";

          if (
            event.relatedTarget.id === "drag_player_icon" ||
            event.relatedTarget.id === "drag_player_icon_mobile"
          ) {
            let dragPlayerIcon = document.getElementById(
              event.relatedTarget.id
            );
            let rect = dragPlayerIcon.getBoundingClientRect();
            pt.x = rect.x + rect.width / 2;
            pt.y = rect.y + rect.width / 2;
            pt = pt.matrixTransform(svg.getScreenCTM().inverse());
            let team = event.relatedTarget.dataset?.team;
            let number = event.relatedTarget.dataset?.number;
            props.dispatch(
              addObject({
                type: "object",
                subType: "player",
                team: team,
                pos: Helper.transposeCoord(
                  metaKey ? [Math.round(pt.x), Math.round(pt.y)] : [pt.x, pt.y],
                  courtRef.current,
                  "SvgToCourt"
                ),
                number: number,
                color: ["defense", "defense_alt"].includes(team)
                  ? Helper.defaultDefenseColor
                  : Helper.defaultColor,
                status: number === "?" ? "EDIT" : "SELECTED",
                stance: dataStance,
              })
            );
          } else if (
            event.relatedTarget.id === "drag_arrow" ||
            event.relatedTarget.id === "drag_arrow_mobile"
          ) {
            let dragArrowIcon = event.relatedTarget;
            let rect = dragArrowIcon.getBoundingClientRect();
            pt.x = rect.x + (rect.width - 60) / 2;
            pt.y = rect.y + 75;
            pt = pt.matrixTransform(svg.getScreenCTM().inverse());

            let endPoint = Helper.correctForOutOfCourt(
              [pt.x + 5, pt.y],
              courtRef.current
            );
            let middlePoint = [(pt.x - 5 + endPoint[0]) / 2, pt.y];
            props.dispatch(
              addObject({
                type: "arrow",
                subType: event.relatedTarget.dataset?.subType,
                shape: event.relatedTarget.dataset?.subType === "pass" ? 0 : 1,
                status: "SELECTED",
                color: Helper.defaultColor,
                s: Helper.transposeCoord(
                  [pt.x - 5, pt.y],
                  courtRef.current,
                  "SvgToCourt"
                ),
                e: Helper.transposeCoord(
                  endPoint,
                  courtRef.current,
                  "SvgToCourt"
                ),
                m: Helper.transposeCoord(
                  middlePoint,
                  courtRef.current,
                  "SvgToCourt"
                ),
              })
            );
          } else if (dataSubType === "handoff") {
            let dragHandoffIcon = event.relatedTarget;
            let rect = dragHandoffIcon.getBoundingClientRect();
            pt.x = rect.x + rect.width / 2;
            pt.y = rect.y + rect.height / 2;
            pt = pt.matrixTransform(svg.getScreenCTM().inverse());
            props.dispatch(
              addObject({
                type: "object",
                subType: "handoff",
                pos: Helper.transposeCoord(
                  [pt.x, pt.y],
                  courtRef.current,
                  "SvgToCourt"
                ),
                color: Helper.defaultColor,
                status: "SELECTED",
              })
            );
          } else if (dataSubType === "cone") {
            let pt = svg.createSVGPoint();
            let dragConeIcon = event.relatedTarget;
            let rect = dragConeIcon.getBoundingClientRect();
            pt.x = rect.x + rect.width / 2;
            pt.y = rect.y + rect.width / 2;
            pt = pt.matrixTransform(svg.getScreenCTM().inverse());
            props.dispatch(
              addObject({
                type: "object",
                subType: "cone",
                pos: Helper.transposeCoord(
                  [pt.x, pt.y],
                  courtRef.current,
                  "SvgToCourt"
                ),
                color: Helper.defaultColor,
                status: "SELECTED",
              })
            );
          } else if (dataSubType === "text") {
            let pt = svg.createSVGPoint();
            let dragTextIcon = event.relatedTarget;
            let rect = dragTextIcon.getBoundingClientRect();
            pt.x = rect.x + rect.width / 2;
            pt.y = rect.y + rect.width / 2;
            pt = pt.matrixTransform(svg.getScreenCTM().inverse());
            let { width, height } = Helper.defaultTextObject;

            props.dispatch(
              addObject({
                ...Helper.defaultTextObject,
                status: event.relatedTarget.dataset?.mobile
                  ? "EDIT_MOBILE"
                  : "EDIT",
                pos: Helper.transposeCoord(
                  [pt.x - width / 2, pt.y - height / 2],
                  courtRef.current,
                  "SvgToCourt"
                ),
              })
            );
          } else if (dataSubType === "rectangle") {
            let pt = svg.createSVGPoint();
            let dragRectangleIcon = event.relatedTarget;
            let rect = dragRectangleIcon.getBoundingClientRect();
            pt.x = rect.x + rect.width / 2;
            pt.y = rect.y + rect.width / 2;
            pt = pt.matrixTransform(svg.getScreenCTM().inverse());
            let { width, height } = Helper.defaultRectangleObject;
            props.dispatch(
              addObject({
                ...Helper.defaultRectangleObject,
                width: courtRef.current.full ? height : width,
                height: courtRef.current.full ? width : height,
                pos: Helper.transposeCoord(
                  courtRef.current.full
                    ? [pt.x - width / 2, pt.y + height / 2]
                    : [pt.x - width / 2, pt.y - height / 2],
                  courtRef.current,
                  "SvgToCourt"
                ),
              })
            );
          } else if (dataSubType === "circle") {
            let pt = svg.createSVGPoint();
            let dragRectangleIcon = event.relatedTarget;
            let rect = dragRectangleIcon.getBoundingClientRect();
            pt.x = rect.x + rect.width / 2;
            pt.y = rect.y + rect.width / 2;
            pt = pt.matrixTransform(svg.getScreenCTM().inverse());
            props.dispatch(
              addObject({
                ...Helper.defaultCircleObject,
                pos: Helper.transposeCoord(
                  courtRef.current.full ? [pt.x, pt.y] : [pt.x, pt.y],
                  courtRef.current,
                  "SvgToCourt"
                ),
              })
            );
          } else if (dataSubType === "triangle") {
            let pt = svg.createSVGPoint();
            let dragTriangleIcon = event.relatedTarget;
            let rect = dragTriangleIcon.getBoundingClientRect();
            pt.x = rect.x + rect.width / 2;
            pt.y = rect.y + rect.width / 2;
            pt = pt.matrixTransform(svg.getScreenCTM().inverse());
            props.dispatch(
              addObject({
                ...Helper.defaultTriangleObject(
                  Helper.transposeCoord(
                    [pt.x, pt.y],
                    courtRef.current,
                    "SvgToCourt"
                  )
                ),
              })
            );
          } else if (dataSubType === "polygon") {
            let pt = svg.createSVGPoint();
            let dragPolygonIcon = event.relatedTarget;
            let rect = dragPolygonIcon.getBoundingClientRect();
            pt.x = rect.x + rect.width / 2;
            pt.y = rect.y + rect.width / 2;
            pt = pt.matrixTransform(svg.getScreenCTM().inverse());
            props.dispatch(
              addObject({
                ...Helper.defaultPolygonObject(
                  Helper.transposeCoord(
                    [pt.x, pt.y],
                    courtRef.current,
                    "SvgToCourt"
                  ),
                  4
                ),
              })
            );
          }
        },
      });
    }

    return () => {
      interact("#" + props.uId + "_court").unset();
    };
  }, []); //eslint-disable-line

  useEffect(() => {
    if (props.editable) {
      document.addEventListener("mouseup", handleMouseUp, false);
      document.addEventListener("mousemove", handleMouseMove, false);
      document
        .getElementById("drawboard_bg")
        ?.addEventListener("mousedown", handleMouseDown, false);

      document.addEventListener("touchend", handleMouseUp, false);
      document.addEventListener("touchmove", handleMouseMove, false);
      document
        .getElementById("drawboard_bg")
        ?.addEventListener("touchstart", handleMouseDown, false);

      return () => {
        document.removeEventListener("mouseup", handleMouseUp, false);
        document.removeEventListener("mousemove", handleMouseMove, false);
        document
          .getElementById("drawboard_bg")
          ?.removeEventListener("mousedown", handleMouseDown, false);
        document.removeEventListener("touchend", handleMouseUp, false);
        document.removeEventListener("touchmove", handleMouseMove, false);
        document
          .getElementById("drawboard_bg")
          ?.removeEventListener("touchstart", handleMouseDown, false);
      };
    }
  }, []); //eslint-disable-line

  const handleMouseDown = (e) => {
    if (!e.target.dataset.register) return;
    if (e.which === 3 || e.button === 2) {
      props.dispatch(unselect());

      return;
    }
    props.dispatch(unselect());
    dispatch({
      type: "MOUSE_DOWN",
      payload: {
        x:
          e.type === "touchstart" || e.type === "touchmove"
            ? e.touches[0].pageX
            : e.pageX,
        y:
          (e.type === "touchstart" || e.type === "touchmove"
            ? e.touches[0].pageY
            : e.pageY) - window.pageYOffset,
      },
    });
  };

  const handleMouseMove = (e) => {
    if (stateRef.current.hold) {
      dispatch({
        type: "MOUSE_MOVE",
        payload: {
          x:
            e.type === "touchstart" || e.type === "touchmove"
              ? e.touches[0].pageX
              : e.pageX,
          y:
            e.type === "touchstart" || e.type === "touchmove"
              ? e.touches[0].pageY
              : e.pageY,
        },
      });
    }
  };

  const handleMouseUp = (e) => {
    if (stateRef.current.selectionBox) {
      closeSelectionBox(
        propsRef.current.phase.objects,
        stateRef.current.selectionBoxOrigin,
        stateRef.current.selectionBoxTarget,
        propsRef.current.court.full,
        objectSizes
      );
    } else if (stateRef.current.hold) {
      dispatch({ type: "MOUSE_UP" });
    }
  };

  let {
    minX: viewBoxX,
    minY: viewBoxY,
    viewBoxWidth,
    viewBoxHeight,
  } = Helper.getMaxCoordinates(props.court);

  let sortedObjects = useMemo(
    () => getSortedObjects(props.phase.objects, props.court),
    [props.phase.objects, props.court]
  );

  let transformedPlayers = Helper.transformObjects(
    props.phase.objects,
    props.court
  ).filter((x) => x.type === "object");

  transformedPlayers.sort((a, b) => (a.status > b.status ? 1 : -1));

  let hoveredArrowIDs = props.phase.objects
    .filter(
      (object) =>
        (object.type === "arrow" || object.subType === "handoff") &&
        ["SELECTED", "HOVERING", "INTERACTING"].includes(object.status)
    )
    .map((object) => object.id);

  let linkedPlayerIDs = props.phase.links
    .filter(
      (link) =>
        hoveredArrowIDs.includes(link.object.id) ||
        (hoveredArrowIDs.includes(link.player.id) &&
          link.object.subType !== "handoff")
    )
    .map((link) => link.player.id);

  let allPlayersNowAndFuture = useMemo(() => {
    let players = Helper.transformObjects(
      props.phase.objects,
      courtRef.current
    ).filter(
      (x) =>
        x.type === "object" && (x.subType === "player" || x.subType === "hoop")
    );
    return [
      ...players,
      ...Helper.getFuturePlayers(
        Helper.transformObjects(props.phase.objects, courtRef.current)
      ),
    ];
  }, [props.phase.objects, courtRef.current]);

  let pixelPerFoot = Helper.safeNumber(
    wrapperWidth /
      (props.court.full
        ? props.court.length + 2 * props.court.sidelines
        : props.court.width + 2 * props.court.sidelines)
  );

  let objectSizes = Helper.getObjectSizes(wrapperWidth, props.court);

  let { playerCircleRadius } = objectSizes.viewbox;

  let playerCircleRadiusRef = useCurrentValue(playerCircleRadius);

  const initialState = {
    hold: false,
    selectionBox: false,
    selectionBoxOrigin: [0, 0],
    selectionBoxTarget: [0, 0],
  };

  const closeSelectionBox = (
    objects,
    origin,
    target,
    fullCourt,
    objectSizes
  ) => {
    let selectedObjects = selectObjects(
      objects,
      origin,
      target,
      fullCourt,
      objectSizes,
      objectSizeFactor
    );
    props.dispatch(selectMultiple(selectedObjects));

    dispatch({ type: "MOUSE_UP" });
  };

  function reducer(state, action) {
    if (action.type === "MOUSE_DOWN") {
      let { x, y } = action.payload;
      return {
        ...state,
        hold: true,
        selectionBoxOrigin: transformToSVG(x, y),
        selectionBoxTarget: transformToSVG(x, y),
      };
    }
    if (action.type === "MOUSE_MOVE") {
      let { x, y } = action.payload;
      return {
        ...state,
        selectionBox: true,
        selectionBoxTarget: transformToSVG(x, y),
      };
    }
    if (action.type === "MOUSE_UP") {
      return {
        ...state,
        selectionBox: false,
        hold: false,
      };
    }

    throw Error("Unknown action.");
  }

  const [state, dispatch] = useReducer(reducer, initialState);

  const stateRef = useCurrentValue(state);
  const propsRef = useCurrentValue(props);

  let selectedObjects = props.phase.objects.filter(
    (x) => x.status === "SELECTED" || x.status === "SELECTED_MULTIPLE"
  );

  const objectSizeFactor = props.objectSizeFactor ?? 1;

  const selectionRect = props.editable
    ? getSelectionRect(
        selectedObjects,
        objectSizes,
        props.court.full,
        2,
        objectSizeFactor
      )
    : null;

  return (
    <div
      ref={wrapper}
      style={{ flex: 1, position: "relative" }}
      id={props.uId + "_wrapper"}
    >
      <PhaseWrapper
        uId={props.uId}
        style={props.style}
        dragHandleProps={props.dragHandleProps}
        horizontal={props.horizontal}
        hoverConfig={props.hoverConfig}
        title={props.title}
        active={false}
        id={props.phase.id}
        dispatch={props.dispatch}
        height={height}
        isMount={props.isMount}
        width={wrapperWidth}
      >
        <Court mirror={props.mirror} pixelPerFoot={pixelPerFoot} {...props}>
          {state.selectionBox && (
            <rect
              id={props.uId + "_selection_box"}
              x={Math.min(
                state.selectionBoxOrigin[0],
                state.selectionBoxTarget[0]
              )}
              y={Math.min(
                state.selectionBoxOrigin[1],
                state.selectionBoxTarget[1]
              )}
              width={Math.abs(
                state.selectionBoxTarget[0] - state.selectionBoxOrigin[0]
              )}
              height={Math.abs(
                state.selectionBoxTarget[1] - state.selectionBoxOrigin[1]
              )}
              fill="rgba(255,255,255,0.6)"
              stroke="rgb(10, 132, 255)"
              strokeWidth={1.5 / pixelPerFoot}
            />
          )}

          <rect
            data-register={props.editable ? true : null}
            id={props.uId + "_court_bg"}
            fill="transparent"
            x={viewBoxX}
            y={viewBoxY}
            width={viewBoxWidth}
            height={viewBoxHeight}
          />

          {sortedObjects.map((object) => {
            let links = props.phase.links.filter(
              (x) => x.object.id === object.id || x.player.id === object.id
            );

            if (object.type === "arrow") {
              let allLinkedPlayers = links
                .map((link) =>
                  Helper.resolveLinkedPlayer(link, props.phase.links)
                )
                .filter((x) => !!x);

              return (
                <Arrow
                  allLinkedPlayers={allLinkedPlayers}
                  objectSizeFactor={objectSizeFactor}
                  objectSizes={objectSizes.viewbox}
                  linkedObjectHovering={
                    linkedPlayerIDs.includes(object.id) && props.editable
                  }
                  animatable={props.animatable}
                  interaction={
                    props.phase.objects.findIndex(
                      (object) => object.status === "INTERACTING"
                    ) !== -1
                  }
                  links={links}
                  dispatch={props.dispatch}
                  court={props.court}
                  pixelPerFoot={pixelPerFoot}
                  editable={props.editable}
                  phaseId={props.id}
                  highlightPossibleLinks={(action) => {
                    Helper.highlightPossibleLinks(
                      action,
                      allPlayersNowAndFuture,
                      playerCircleRadiusRef.current * objectSizeFactor
                    );
                  }}
                  uId={props.uId + "_" + object.id}
                  key={props.uId + "_" + object.id}
                  {...object}
                />
              );
            } else if (object.subType === "text") {
              return (
                <Text
                  objectSizes={objectSizes.viewbox}
                  court={props.court}
                  wrapperWidth={wrapperWidth}
                  pixelPerFoot={pixelPerFoot}
                  animatable={props.animatable}
                  dispatch={props.dispatch}
                  editable={props.editable}
                  phaseId={props.id}
                  uId={props.uId + "_" + object.id}
                  key={props.uId + "_" + object.id}
                  {...object}
                />
              );
            } else if (object.subType === "rectangle") {
              return (
                <Rectangle
                  objectSizes={objectSizes.viewbox}
                  court={props.court}
                  wrapperWidth={wrapperWidth}
                  pixelPerFoot={pixelPerFoot}
                  animatable={props.animatable}
                  dispatch={props.dispatch}
                  editable={props.editable}
                  phaseId={props.id}
                  uId={props.uId + "_" + object.id}
                  key={props.uId + "_" + object.id}
                  {...object}
                />
              );
            } else if (object.subType === "circle") {
              return (
                <Circle
                  objectSizes={objectSizes.viewbox}
                  court={props.court}
                  wrapperWidth={wrapperWidth}
                  pixelPerFoot={pixelPerFoot}
                  animatable={props.animatable}
                  dispatch={props.dispatch}
                  editable={props.editable}
                  phaseId={props.id}
                  uId={props.uId + "_" + object.id}
                  key={props.uId + "_" + object.id}
                  {...object}
                />
              );
            } else if (["triangle", "polygon"].includes(object.subType)) {
              return (
                <Polygon
                  objectSizes={objectSizes.viewbox}
                  court={props.court}
                  wrapperWidth={wrapperWidth}
                  pixelPerFoot={pixelPerFoot}
                  animatable={props.animatable}
                  dispatch={props.dispatch}
                  editable={props.editable}
                  phaseId={props.id}
                  uId={props.uId + "_" + object.id}
                  key={props.uId + "_" + object.id}
                  {...object}
                />
              );
            } else if (
              ["cone", "player", "handoff", "hoop"].includes(object.subType)
            ) {
              return (
                <Player
                  links={links}
                  highlightHandoffPlayers={(point, radius) =>
                    Helper.highlightHandoffPlayers(
                      point,
                      allPlayersNowAndFuture,
                      props.uId + "_" + object.id,
                      radius,
                      courtRef.current
                    )
                  }
                  getLinkedHandoffPaths={(point, radius) =>
                    Helper.getLinkedHandoffPaths(
                      point,
                      allPlayersNowAndFuture,
                      radius,
                      links,
                      courtRef.current
                    )
                  }
                  /*
                  checkForHandoffPlayers={(point, radius) =>
                    Helper.checkForHandoffPlayers(
                      point,
                      allPlayersNowAndFuture,
                      radius,
                      courtRef.current
                    )
                  }*/
                  objectSizeFactor={objectSizeFactor}
                  objectSizes={objectSizes.viewbox}
                  court={props.court}
                  wrapperWidth={wrapperWidth}
                  pixelPerFoot={pixelPerFoot}
                  animatable={props.animatable}
                  linkedObjectHovering={
                    linkedPlayerIDs.includes(object.id) && props.editable
                  }
                  dispatch={props.dispatch}
                  editable={props.editable}
                  phaseId={props.id}
                  uId={props.uId + "_" + object.id}
                  key={props.uId + "_" + object.id}
                  {...object}
                />
              );
            } else {
              return null;
            }
          })}

          {selectionRect && (
            <SelectionRectangle
              manualPositioningContextMenu={props.manualPositioningContextMenu}
              court={props.court}
              affectedObjects={selectedObjects}
              selectionRect={selectionRect}
              pixelPerFoot={pixelPerFoot}
            />
          )}
        </Court>
      </PhaseWrapper>
    </div>
  );
}

Phase.defaultProps = {
  responsive: true,
  horizontal: false,
  animatable: false,
  objectSizeFactor: 1,
};

Phase.propTypes = {
  objectSizeFactor: PropTypes.number.isRequired,
};

export default Phase;
